import { useState } from 'react';
import { AddFieldDropdown, CommittedField, DraftCard } from './components';
import { newDefinition } from './helpers';
import type { CustomFieldDraft, FieldDefinition } from './types';

export type { BuilderFieldType, CustomFieldDraft, FieldDefinition } from './types';
export {
  customFieldDraftsToParams,
  customFieldsHaveMissingRequired,
  valueIsEmpty,
} from './helpers';
export { CommittedField } from './components';

let fieldCounter = 0;

export const CustomFieldsBuilder = ({
  fields,
  onChange,
  companyId,
  onAddSharedField,
}: {
  fields: CustomFieldDraft[];
  onChange: (fields: CustomFieldDraft[]) => void;
  companyId: number;
  // A "save for future" field is hoisted to the wizard's session-shared state (so it
  // shows on every form for the attribute) instead of staged locally; one-off fields
  // stay in `fields`. Nothing is persisted until final submit.
  onAddSharedField?: (definition: FieldDefinition) => void;
}) => {
  const [definition, setDefinition] = useState<FieldDefinition | null>(null);

  const patchField = (id: string, patch: Partial<CustomFieldDraft>) =>
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const removeField = (id: string) =>
    onChange(fields.filter((f) => f.id !== id));

  const commit = () => {
    if (!definition || definition.label.trim().length === 0) return;
    if (definition.saveForFuture && onAddSharedField) {
      onAddSharedField({ ...definition, label: definition.label.trim() });
      setDefinition(null);
      return;
    }
    fieldCounter += 1;
    onChange([
      ...fields,
      {
        id: `cf-${fieldCounter}`,
        type: definition.type,
        label: definition.label.trim(),
        isRequired: definition.isRequired,
        saveForFuture: definition.saveForFuture,
        value: undefined,
      },
    ]);
    setDefinition(null);
  };

  return (
    <div className="flex flex-col gap-3xl">
      {fields.map((field) => (
        <CommittedField
          key={field.id}
          field={field}
          companyId={companyId}
          onChange={(patch) => patchField(field.id, patch)}
          onRemove={() => removeField(field.id)}
        />
      ))}
      {definition ? (
        <DraftCard
          definition={definition}
          onPatch={(patch) =>
            setDefinition((d) => (d ? { ...d, ...patch } : d))
          }
          onAdd={commit}
          onDiscard={() => setDefinition(null)}
        />
      ) : (
        <AddFieldDropdown onAdd={(type) => setDefinition(newDefinition(type))} />
      )}
    </div>
  );
};
